import React, { useMemo } from 'react';
import Button from '../Button/index';
import { FaFileAlt } from 'react-icons/fa';
import { RoundFixedNumber } from '../../services/functions';
import { Container } from './styles';
import { ICON_COLORS } from './colors.jsx';
import Swal from 'sweetalert2';

/** * Versão ADAPTATIVA: 
 * Se o valor no BANCO (dVal) for nulo/vazio, ignoramos a restrição dessa dimensão.
 * Isso resolve o problema da seção Carbon Footprint onde o banco armazena IDs nulos.
 */
function sameEvidenceDim(dVal, reqVal) {
  const isEmpty = (v) => v === undefined || v === null || v === '' || v === 0 || v === '0';
  
  // Se no banco o campo está vazio, permitimos o match (ignora o filtro dessa coluna)
  if (isEmpty(dVal)) return true;
  
  // Se no banco existe um valor (ex: Row ou Column específica), comparamos estritamente
  return String(dVal) === String(reqVal);
}

const EvidenceButton = ({
  evidenceRequest,
  setEvidenceRequest,
  setShowUploadEvidence,
  isMandatory = false,
  kpiEvidences = null,
  disabled = false,
  buttonText = 'Evidence',
  moduleObject,
}) => {
  const { validEvidence, iconColor } = useMemo(() => {
    if (!kpiEvidences || !evidenceRequest) {
      return { validEvidence: null, iconColor: ICON_COLORS.default };
    }

    // Normalização de IDs (Aceita camelCase ou PascalCase)
    const fuelReq = evidenceRequest.fuelTypeID ?? evidenceRequest.fuelTypeId ?? null;
    const countryReq = evidenceRequest.countryID ?? evidenceRequest.countryId ?? null;
    const barnReq = evidenceRequest.curingBarnID ?? evidenceRequest.curingBarnId ?? null;

    // 1. Filtragem Inteligente
    const foundEvidences = kpiEvidences.filter(d => {
      // O ID do KPI deve ser sempre igual
      if (d.subSectionKPIID !== evidenceRequest.subSectionKPIID) return false;

      /**
       * Validação de Dimensões com Fallback para NULL:
       * Se d.fuelTypeID for NULL no banco (conforme vimos no seu SQL), 
       * o sameEvidenceDim retornará TRUE, permitindo que a evidência seja encontrada.
       */
      return (
        sameEvidenceDim(d.row, evidenceRequest.row) &&
        sameEvidenceDim(d.column, evidenceRequest.column) &&
        sameEvidenceDim(d.cultureTypeID, evidenceRequest.cultureTypeID) &&
        sameEvidenceDim(d.fuelTypeID, fuelReq) &&
        sameEvidenceDim(d.curingBarnID, barnReq) &&
        sameEvidenceDim(d.countryID, countryReq)
      );
    });

    // 2. Validação de Valor (Compara input da tela com valor salvo)
    const currentVal = RoundFixedNumber(evidenceRequest.subSectionKPIValue, 2);
    
    const match = foundEvidences.find(x => {
        const valSaved = RoundFixedNumber(x.value, 2);
        const kpiValSaved = RoundFixedNumber(x.subSectionKPIValue, 2);
        
        // Match se o valor do arquivo bater com o que está digitado na tela
        return valSaved === currentVal && kpiValSaved === currentVal 
          && (evidenceRequest.evidenceRefValidateValue !== null && evidenceRequest.evidenceRefValidateValue !== '' ? x.farmersCount == evidenceRequest.evidenceRefValidateValue : x.farmersCount == evidenceRequest.farmersCount);
    });

    // 3. Status das Cores
    let color = ICON_COLORS.default;
    
    if (match && match.file) { 
      // Se achou o registro E existe um nome de arquivo na coluna 'File'
      color = match.isValidated ? ICON_COLORS.closed : ICON_COLORS.open;
    } else if (foundEvidences.length > 0) {
      // Se existe arquivo no banco, mas o valor do input da tela divergiu
      color = ICON_COLORS.open;
    }

    return { validEvidence: match, iconColor: color };
  }, [kpiEvidences, evidenceRequest]);

  // ID único para o ícone
  const buttonIconId = `evidence-button-icon-${evidenceRequest.subSectionKPIID}${
    evidenceRequest.column != null && evidenceRequest.column !== '' ? `-c${evidenceRequest.column}` : ''
  }${
    evidenceRequest.row != null && evidenceRequest.row !== '' ? `-r${evidenceRequest.row}` : ''
  }`;

  function openModal() {
    if (moduleObject.isEditable || (!moduleObject.isEditable && validEvidence)) {
      if (!validEvidence) {
        setEvidenceRequest(evidenceRequest);
      } else {
        setEvidenceRequest({
          ...validEvidence,
          ...evidenceRequest,
          file: validEvidence.file ?? evidenceRequest.file,
          value: validEvidence.value ?? evidenceRequest.value,
          farmersCount: validEvidence.farmersCount ?? evidenceRequest.farmersCount,
          isValidated: validEvidence.isValidated ?? evidenceRequest.isValidated,
          subSectionKPIValue: evidenceRequest.subSectionKPIValue,
          evidenceRefValidateValue: evidenceRequest.evidenceRefValidateValue,
        });
      }
      setShowUploadEvidence(true);
    } else if (!moduleObject.isEditable && !validEvidence) {
      Swal.fire({
        title: 'Evidence not found!',
        text: 'There is no evidence for this KPI.',
        icon: 'warning',
        confirmButtonColor: '#112A61',
        confirmButtonText: 'Ok',
      });
    }
  }

  return (
    <Container onClick={openModal} disabled={disabled}>
      <Button disabled={disabled} style={{ position: 'relative' }}>
        {buttonText}
        <FaFileAlt
          color={iconColor}
          id={buttonIconId}
          className="icon-chat"
          size={30}
        />
        {isMandatory && <div className="isMandatory"></div>}
      </Button>
    </Container>
  );
};

export default EvidenceButton;